import { alpha, Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';

import { type ActivityUsageGranularity, useActivityModels, useActivityUsage } from '../../api/index.js';
import { sumUsageByPatientMonth, usageMonthKey } from '../Activity/usageMonthlyTotals.js';
import Table from '../Table/Table.js';

const OVER_TARGET_ROW_CLASS = 'usage-row--over-target';
const MONTH_VS_TARGET_COLUMN: GridColDef = {
  field: 'monthVsTarget',
  headerName: 'Month vs target',
  width: 150,
  align: 'right',
  headerAlign: 'right',
};

type SummaryPeriod = 'today' | 'month';

interface PatientActivityUsageProps {
  patientId: string;
}

const PatientActivityUsage: FC<PatientActivityUsageProps> = ({ patientId }) => {
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('today');
  const [granularity, setGranularity] = useState<ActivityUsageGranularity>('day');
  const [anchorDate, setAnchorDate] = useState<DateTime>(DateTime.now());
  const models = useActivityModels();

  const now = useMemo(() => DateTime.now(), []);
  const isDaily = granularity === 'day';

  const summaryRange = useMemo(() => {
    const from = summaryPeriod === 'today' ? now.startOf('day') : now.startOf('month');
    return { from: from.toISO() ?? '', to: now.toISO() ?? '' };
  }, [summaryPeriod, now]);

  const summaryUsage = useActivityUsage(
    { patientId, from: summaryRange.from, to: summaryRange.to, granularity: summaryPeriod === 'today' ? 'day' : 'month' },
    { refetchInterval: 60_000, refetchIntervalInBackground: true },
  );

  // Daily lists the days of the chosen month; monthly lists the months of the chosen year.
  const breakdownRange = useMemo(() => {
    const unit = isDaily ? 'month' : 'year';
    const periodEnd = anchorDate.endOf(unit);
    return {
      from: anchorDate.startOf(unit).toISO() ?? '',
      to: (periodEnd > now ? now : periodEnd).toISO() ?? '',
    };
  }, [anchorDate, isDaily, now]);

  const breakdownUsage = useActivityUsage(
    { patientId, from: breakdownRange.from, to: breakdownRange.to, granularity },
    { keepPreviousData: true },
  );

  const totals = useMemo(() => {
    const sum = (summaryUsage.data?.items ?? []).reduce(
      (acc, item) => ({
        events: acc.events + item.events,
        inputTokens: acc.inputTokens + item.inputTokens,
        outputTokens: acc.outputTokens + item.outputTokens,
        costUsd: acc.costUsd + item.costUsd,
        latencyWeighted: acc.latencyWeighted + item.avgLatencyMs * item.events,
      }),
      { events: 0, inputTokens: 0, outputTokens: 0, costUsd: 0, latencyWeighted: 0 },
    );
    return { ...sum, avgLatencyMs: sum.events > 0 ? Math.round(sum.latencyWeighted / sum.events) : 0 };
  }, [summaryUsage.data]);

  const monthlyTargetUsd = breakdownUsage.data?.monthlyTargetUsd ?? summaryUsage.data?.monthlyTargetUsd ?? null;
  const summaryOverTarget =
    summaryPeriod === 'month' && monthlyTargetUsd !== null && totals.costUsd > monthlyTargetUsd;

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'day', headerName: isDaily ? 'Day' : 'Month', width: 120 },
      {
        field: 'modelLabel',
        headerName: 'Model',
        flex: 1,
        minWidth: 260,
        renderCell: (params) => (
          <Stack sx={{ minWidth: 0, justifyContent: 'center', height: '100%' }}>
            <Typography variant={'body1'} noWrap>
              {params.row.modelLabel}
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'} noWrap>
              {params.row.modelId}
            </Typography>
          </Stack>
        ),
      },
      { field: 'events', headerName: 'Events', width: 100, align: 'right', headerAlign: 'right' },
      { field: 'costUsd', headerName: 'Cost (est.)', width: 120, align: 'right', headerAlign: 'right' },
      ...(isDaily ? [] : [MONTH_VS_TARGET_COLUMN]),
    ],
    [isDaily],
  );

  const rows: GridRowsProp = useMemo(() => {
    const items = breakdownUsage.data?.items ?? [];
    const monthTotals = sumUsageByPatientMonth(items);
    return items.map((item) => {
      const monthTotal = monthTotals.get(usageMonthKey(item)) ?? 0;
      return {
        id: `${item.day}::${item.modelId}`,
        day: item.day,
        modelId: item.modelId,
        modelLabel: models.data?.items.find((model) => model.id === item.modelId)?.label ?? item.modelId,
        events: item.events.toLocaleString(),
        costUsd: `$${item.costUsd.toFixed(2)}`,
        monthVsTarget: monthlyTargetUsd === null
          ? '—'
          : `$${monthTotal.toFixed(2)} / $${monthlyTargetUsd.toFixed(0)}`,
        overTarget: !isDaily && monthlyTargetUsd !== null && monthTotal > monthlyTargetUsd,
      };
    });
  }, [breakdownUsage.data, models.data, monthlyTargetUsd, isDaily]);

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction={'row'} alignItems={'center'} spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
          <Typography variant={'h5'} component={'h3'}>
            AI usage
          </Typography>
          <ToggleButtonGroup
            size={'small'}
            exclusive
            sx={{ ml: 'auto' }}
            value={summaryPeriod}
            onChange={(_event, value: SummaryPeriod | null) => value && setSummaryPeriod(value)}
          >
            <ToggleButton value={'today'}>Today</ToggleButton>
            <ToggleButton value={'month'}>This month</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <UsageStat
            label={'Cost (est.)'}
            value={`$${totals.costUsd.toFixed(2)}`}
            caption={
              summaryPeriod === 'month' && monthlyTargetUsd !== null
                ? `${summaryOverTarget ? 'over' : 'of'} the $${monthlyTargetUsd.toFixed(0)} / month target`
                : undefined
            }
            highlight={summaryOverTarget}
            emphasised
          />
          <UsageStat label={'Events'} value={totals.events.toLocaleString()} />
          <UsageStat label={'Input tokens'} value={formatThousands(totals.inputTokens)} />
          <UsageStat label={'Output tokens'} value={formatThousands(totals.outputTokens)} />
          <UsageStat label={'Avg latency'} value={totals.avgLatencyMs > 0 ? `${totals.avgLatencyMs} ms` : '—'} />
        </Box>
        <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 1.5 }}>
          Estimated against current Bedrock list pricing — refer to the AWS Budget for billing-grade numbers.
        </Typography>
      </Box>

      <Box>
        <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
          <Typography variant={'h5'} component={'h3'}>
            Breakdown
          </Typography>
          <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ ml: 'auto' }}>
            <DatePicker
              label={isDaily ? 'Month' : 'Year'}
              views={isDaily ? ['year', 'month'] : ['year']}
              openTo={isDaily ? 'month' : 'year'}
              value={anchorDate}
              maxDate={now}
              onChange={(value: DateTime | null) => value && setAnchorDate(value)}
              slotProps={{ textField: { size: 'small', sx: { width: isDaily ? 180 : 120 } } }}
            />
            <ToggleButtonGroup
              size={'small'}
              exclusive
              value={granularity}
              onChange={(_event, value: ActivityUsageGranularity | null) => value && setGranularity(value)}
            >
              <ToggleButton value={'day'}>Daily</ToggleButton>
              <ToggleButton value={'month'}>Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
        <Box
          sx={{
            [`& .${OVER_TARGET_ROW_CLASS}`]: {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.14) },
            },
          }}
        >
          <Table
            columns={columns}
            rows={rows}
            pageSize={10}
            pageSizeOptions={[10, 25, 50]}
            rowHeight={60}
            isLoading={breakdownUsage.isLoading}
            getRowClassName={(params) => (params.row.overTarget ? OVER_TARGET_ROW_CLASS : '')}
            NoRowsOverlayText={
              isDaily
                ? `No usage recorded in ${anchorDate.toFormat('LLLL yyyy')}.`
                : `No usage recorded in ${anchorDate.toFormat('yyyy')}.`
            }
            disableColumnMenu
          />
        </Box>
        {!isDaily && monthlyTargetUsd !== null ? (
          <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 1 }}>
            Months over the ${monthlyTargetUsd.toFixed(0)} per-patient target are highlighted; the daily view
            is not compared because captures only run inside check-in windows.
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
};

function formatThousands(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

interface UsageStatProps {
  label: string;
  value: string;
  caption?: string;
  highlight?: boolean;
  emphasised?: boolean;
}

const UsageStat: FC<UsageStatProps> = ({ label, value, caption, highlight, emphasised }) => (
  <Stack sx={{ minWidth: 0 }}>
    <Typography variant={'body2'} color={'text.secondary'} noWrap>
      {label}
    </Typography>
    <Typography
      variant={emphasised ? 'h3' : 'h4'}
      sx={{ lineHeight: 1.2, color: highlight ? 'error.main' : undefined }}
      noWrap
    >
      {value}
    </Typography>
    {caption ? (
      <Typography variant={'caption'} color={highlight ? 'error.main' : 'text.secondary'} noWrap>
        {caption}
      </Typography>
    ) : null}
  </Stack>
);

export default PatientActivityUsage;
